import { useCallback, useEffect, useRef } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Color, FontSize, TextStyle } from "@tiptap/extension-text-style";
import {
  Bold,
  Heading2,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
} from "lucide-react";
import { Select } from "./Select";
import { createMention } from "./mentionExtension";

// 富文本編輯器。業主 2026-09-18:「會議記錄系統撰寫要跟 office WORD 一樣」。
//
// **用 TipTap 不是手刻 contentEditable**,理由只有一個但夠了:
// 你一定會從 Word 貼東西進來,而 Word 貼出來的是一大坨 `<o:p>` 與 `mso-*`
// 樣式。手刻的版本會先能用,然後在第一次貼上就壞掉。
// `document.execCommand` 也已經廢棄,各瀏覽器產出的 HTML 還不一樣
//(有的到今天仍然吐 `<font>`)。
//
// **消毒不在這裡做。** 這裡產的 HTML 一律送到後端,由 `app/sanitize.py` 洗過
// 才進資料庫 —— 前端這一層繞得過去(直接打 API),後端那一層繞不過去。
// 兩邊各寫一套規則的話,沒有人說得出實際生效的是哪一套。

/** 工具列上一顆鈕。**按下去的當下要看得出它是開著的**(粗體正在生效)。 */
function Tool({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active ?? false}
      // `onMouseDown` 擋掉預設行為:不擋的話按鈕會搶走焦點,
      // 游標選取的那一段就沒了,粗體會套到空的地方。
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-[var(--radius)] border text-[var(--text)] ${
        active
          ? "border-[var(--accent)] bg-[var(--surface-2)]"
          : "border-transparent hover:bg-[var(--surface-2)]"
      }`}
    >
      {children}
    </button>
  );
}

const COLORS: [string, string][] = [
  ["#111827", "黑"],
  ["#c62828", "紅"],
  ["#1565c0", "藍"],
  ["#2e7d32", "綠"],
  ["#ef6c00", "橘"],
];

/** 字級。**不給任意數值** —— 十七種字級的文件沒有人讀得下去。 */
const SIZES: [string, string][] = [
  ["", "正常"],
  ["0.875rem", "小"],
  ["1.25rem", "大"],
  ["1.5rem", "特大"],
];

function Toolbar({
  editor,
  onPickImage,
  uploading,
}: {
  editor: Editor;
  onPickImage: () => void;
  uploading: boolean;
}) {
  const addLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const href = window.prompt("連結網址", previous ?? "https://");
    if (href === null) return;
    if (href.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-[var(--border)] bg-[var(--surface-2)] px-1.5 py-1">
      <Tool label="粗體" active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold size={15} aria-hidden="true" />
      </Tool>
      <Tool label="斜體" active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic size={15} aria-hidden="true" />
      </Tool>
      <Tool label="底線" active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon size={15} aria-hidden="true" />
      </Tool>
      <Tool label="刪除線" active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough size={15} aria-hidden="true" />
      </Tool>

      <span className="mx-1 h-5 w-px bg-[var(--border)]" aria-hidden="true" />

      <Tool label="段落標題" active={editor.isActive("heading", { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 size={15} aria-hidden="true" />
      </Tool>
      <Tool label="項目符號" active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List size={15} aria-hidden="true" />
      </Tool>
      <Tool label="編號清單" active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered size={15} aria-hidden="true" />
      </Tool>
      <Tool label="引用" active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote size={15} aria-hidden="true" />
      </Tool>

      <span className="mx-1 h-5 w-px bg-[var(--border)]" aria-hidden="true" />

      <Tool label="連結" active={editor.isActive("link")} onClick={addLink}>
        <LinkIcon size={15} aria-hidden="true" />
      </Tool>
      <Tool label="插入圖片" onClick={onPickImage}>
        <ImagePlus size={15} aria-hidden="true" />
      </Tool>
      {uploading ? (
        <span className="px-1 text-xs text-[var(--text-3)]">上傳中…</span>
      ) : null}

      <span className="mx-1 h-5 w-px bg-[var(--border)]" aria-hidden="true" />

      {/* 用共用的 `Select`,不手刻 —— `tests/filter-row-align.test.tsx` 守著這件事,
          而它守的是對的:手刻的那幾個高度與焦點樣式都會慢慢跟系統其他地方分岔。
          工具列上不給 label,靠 `aria-label`。 */}
      <Select
        aria-label="字級"
        className="w-20 text-xs"
        value={(editor.getAttributes("textStyle").fontSize as string) ?? ""}
        options={SIZES.map(([value, label]) => ({ value, label }))}
        onChange={(event) => {
          const size = event.target.value;
          const chain = editor.chain().focus();
          if (size) chain.setFontSize(size).run();
          else chain.unsetFontSize().run();
        }}
      />

      {/* 顏色不可以只靠色塊 —— 每一顆都帶 `aria-label` 說得出是什麼顏色。 */}
      {COLORS.map(([hex, name]) => (
        <button
          key={hex}
          type="button"
          title={name}
          aria-label={`文字顏色:${name}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.chain().focus().setColor(hex).run()}
          className="h-5 w-5 rounded-full border border-[var(--border-strong)]"
          style={{ background: hex }}
        />
      ))}

      <span className="mx-1 h-5 w-px bg-[var(--border)]" aria-hidden="true" />

      <Tool label="復原" onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 size={15} aria-hidden="true" />
      </Tool>
      <Tool label="重做" onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 size={15} aria-hidden="true" />
      </Tool>
    </div>
  );
}

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /**
   * 把一個檔案變成網址。回 `null` 代表上傳失敗 —— 呼叫端負責說為什麼。
   *
   * **不寫死成某一支 API**:這個元件不知道自己被用在會議紀錄還是別的地方,
   * 而知道的那一方本來就在呼叫端。
   */
  onUploadImage?: (file: File) => Promise<string | null>;
  label?: string;
  /** 編輯區最少多高。會議可能很長,預設給得夠。 */
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  onUploadImage,
  label = "內容",
  minHeight = "24rem",
}: RichTextEditorProps) {
  const filePicker = useRef<HTMLInputElement | null>(null);
  const uploading = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      // 字級與顏色都走 `style` 屬性 —— 與後端 `sanitize.STYLES` 的白名單對齊。
      // 白名單裡沒有的屬性後端會洗掉,畫面上就會變回預設,所以兩邊要對。
      FontSize,
      Color,
      Link.configure({
        openOnClick: false,
        // **`javascript:` 要在這裡就擋掉**,不要只靠後端 ——
        // 使用者在編輯中就會看到那個連結,而看到就可能按下去。
        protocols: ["http", "https", "mailto", "tel"],
      }),
      Image.configure({ inline: false }),
      // 內文裡的 @ 提及(業主 2026-09-19:「直接可以在內文提及」)。
      createMention(),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        // 內距也移到這一層 —— 留在外框上的話,點在內距裡一樣點不到。
        class: "px-3 py-2",
        style: `min-height: ${minHeight}`,
      },
    },
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  });

  // 外面換了一份內容(例如切到另一篇紀錄)才要重設。
  // **不比對就 setContent 的話,每打一個字游標都會跳回開頭。**
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, value]);

  const insert = useCallback(
    async (file: File) => {
      if (!editor || !onUploadImage || uploading.current) return;
      uploading.current = true;
      try {
        const url = await onUploadImage(file);
        if (url) editor.chain().focus().setImage({ src: url }).run();
      } finally {
        uploading.current = false;
      }
    },
    [editor, onUploadImage],
  );

  // 貼上與拖放 —— **手機拍完直接貼進來是最常見的動線。**
  useEffect(() => {
    if (!editor || !onUploadImage) return;
    const dom = editor.view.dom;

    const onPaste = (event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.files ?? [])
        .find((one) => one.type.startsWith("image/"));
      if (!file) return;           // 貼的是文字 —— 交給 TipTap 自己處理
      event.preventDefault();
      void insert(file);
    };
    const onDrop = (event: DragEvent) => {
      const file = Array.from(event.dataTransfer?.files ?? [])
        .find((one) => one.type.startsWith("image/"));
      if (!file) return;
      event.preventDefault();
      void insert(file);
    };

    dom.addEventListener("paste", onPaste as EventListener);
    dom.addEventListener("drop", onDrop as EventListener);
    return () => {
      dom.removeEventListener("paste", onPaste as EventListener);
      dom.removeEventListener("drop", onDrop as EventListener);
    };
  }, [editor, onUploadImage, insert]);

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-[var(--text)]">{label}</span>
      <div className="overflow-hidden rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)]">
        <Toolbar
          editor={editor}
          uploading={uploading.current}
          onPickImage={() => filePicker.current?.click()}
        />
        {/* **高度加在可編輯的那一層,不是外框。**
            加在外框上的話,contenteditable 只有一行高 —— 點第一行以下打到的
            是外框,游標不會出現(業主 2026-09-19:「要很準確地移到第一行」)。
            `editorProps.attributes` 會把 style 掛到 ProseMirror 自己身上。 */}
        <EditorContent editor={editor} aria-label={label} className="nq-prose" />
      </div>
      <input
        ref={filePicker}
        type="file"
        accept="image/*"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // 值清掉,不然同一個檔連選兩次不會觸發。
          event.target.value = "";
          if (file) void insert(file);
        }}
      />
    </div>
  );
}
